import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { globalSearch } from "../api/search";
import { getActiveRole } from "../utils/auth";
import { normalizeUserViewLink } from "../utils/userRoutes";

function roleBasePath(role) {
    switch (role) {
        case "MANAGER": return "/manager";
        case "COACH": return "/coach";
        case "STUDENT": return "/student";
        default: return "/admin";
    }
}

function resolvePath(hit, role) {
    if (!hit?.path) return null;
    const base = roleBasePath(role);
    let path = hit.path;
    if (path.startsWith("/admin/")) {
        path = path.replace("/admin", base);
    }
    // Coaches have no batch edit routes — send them to sessions instead of a 404.
    if (role === "COACH" && /\/batches\/\d+/.test(path)) {
        return "/coach/sessions";
    }
    return path;
}

/** Batch edit routes use /batches/:id/edit in this app */
function normalizePath(path) {
    if (!path) return path;
    const profilePath = normalizeUserViewLink(path);
    if (profilePath !== path) return profilePath;
    const batchMatch = path.match(/^(\/(?:admin|manager|coach))\/batches\/(\d+)$/);
    if (batchMatch) {
        return `${batchMatch[1]}/batches/${batchMatch[2]}/edit`;
    }
    return path;
}

export default function GlobalSearch() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const role = getActiveRole() || "ADMIN";
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [results, setResults] = useState({ students: [], coaches: [], batches: [] });
    const debounceRef = useRef(null);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    const runSearch = useCallback(async (term) => {
        const query = (term || "").trim();
        if (query.length < 2) {
            const empty = { students: [], coaches: [], batches: [] };
            setResults(empty);
            setError("");
            setOpen(false);
            return empty;
        }
        setLoading(true);
        setError("");
        setOpen(true);
        try {
            const data = await globalSearch(query);
            const normalized = {
                students: data?.students || [],
                coaches: data?.coaches || [],
                batches: data?.batches || [],
            };
            setResults(normalized);
            return normalized;
        } catch (err) {
            console.error("Global search failed:", err);
            const empty = { students: [], coaches: [], batches: [] };
            setResults(empty);
            setError(
                err?.response?.status === 404
                    ? t('search.apiUnavailable')
                    : t('search.failed')
            );
            return empty;
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (q.trim().length < 2) {
            setResults({ students: [], coaches: [], batches: [] });
            setError("");
            setOpen(false);
            return;
        }
        debounceRef.current = setTimeout(() => runSearch(q), 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [q, runSearch]);

    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const allHits = [
        ...(results.students || []).map((h) => ({ ...h, group: "Students" })),
        ...(results.coaches || []).map((h) => ({ ...h, group: "Coaches" })),
        ...(results.batches || []).map((h) => ({ ...h, group: "Batches" })),
    ];

    const hasHits = allHits.length > 0;

    const go = (hit) => {
        const path = normalizePath(resolvePath(hit, role));
        if (path) {
            navigate(path);
            setOpen(false);
            setQ("");
        }
    };

    const handleEnter = async (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const data = await runSearch(q);
        const hits = [
            ...(data.students || []),
            ...(data.coaches || []),
            ...(data.batches || []),
        ];
        if (hits.length > 0) {
            go(hits[0]);
        }
    };

    const renderGroup = (title, items) => {
        if (!items?.length) return null;
        return (
            <div className="py-1">
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{title}</div>
                {items.map((hit) => (
                    <button
                        key={`${title}-${hit.id}`}
                        type="button"
                        onClick={() => go(hit)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 rounded-lg text-sm text-slate-800"
                    >
                        <div className="font-medium truncate">{hit.name}</div>
                        {hit.email && <div className="text-xs text-slate-500 truncate">{hit.email}</div>}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div ref={wrapRef} className="relative hidden sm:block w-full max-w-xs lg:max-w-sm">
            <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => q.trim().length >= 2 && setOpen(true)}
                onKeyDown={handleEnter}
                placeholder={t('search.placeholder')}
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-500 shadow-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none"
                aria-label={t('search.aria')}
                aria-expanded={open}
                aria-autocomplete="list"
            />
            <svg className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {open && q.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 z-[200] rounded-xl border border-slate-200 bg-white shadow-xl max-h-80 overflow-y-auto">
                    {loading && <div className="px-3 py-3 text-sm text-slate-500">{t('search.searching')}</div>}
                    {error && !loading && (
                        <div className="px-3 py-3 text-sm text-red-600">{error}</div>
                    )}
                    {!loading && !error && !hasHits && (
                        <div className="px-3 py-3 text-sm text-slate-500">{t('search.noResults', { q })}</div>
                    )}
                    {!loading && !error && hasHits && (
                        <>
                            {renderGroup(t('search.students'), results.students)}
                            {renderGroup(t('search.coaches'), results.coaches)}
                            {renderGroup(t('search.batches'), results.batches)}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
